import type { FC } from 'react';
import { Select, chakraComponents } from 'chakra-react-select';
import type { ChakraStylesConfig, GroupBase, SelectComponentsConfig } from 'chakra-react-select';
import { GetIcon } from '../../icons';
import type { SelectOptions } from '../types';

const chakraStyles: ChakraStylesConfig<SelectOptions, true, GroupBase<SelectOptions>> = {
    dropdownIndicator: (provided) => ({
        ...provided,
        p: 0,
        w: '40px',
        background: 'transparent',
    }),
    control: (provided, state) => ({
        ...provided,
        height: '40px',
        minHeight: '40px',
        fontFamily: 'TT Norms Pro',
        fontSize: '1rem',
        lineHeight: '1.375rem',
        letterSpacing: '0.03125rem',
        background: 'white',
        borderRadius: 0,
        borderBottom: state.menuIsOpen ? 'none' : '1px solid',
        color: 'slt-pepper.1000',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '40px',
        padding: '0 6px',
    }),
    menu: (provided) => ({
        ...provided,
        my: 0,
        borderWidth: '1px',
        borderColor: 'slt-pepper.1000',
        borderTop: 'none',
        borderBottomRadius: '4px',
    }),
    option: (provided, state) => ({
        ...provided,
        fontFamily: 'TT Norms Pro',
        fontSize: '1rem',
        lineHeight: '1.375rem',
        letterSpacing: '0.03125rem',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 0,
        background: state.isSelected ? '#F9F9F9 !important' : 'unset',
        color: 'slt-pepper.1000 !important',
    }),
    menuList: (provided) => ({
        ...provided,
        borderWidth: 0,
        paddingTop: 0,
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: '40px',
    }),
    indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
    }),
};

const customComponents: SelectComponentsConfig<SelectOptions, true, GroupBase<SelectOptions>> = {
    Option: ({ children, ...props }) => (
        <chakraComponents.Option {...props}>
            {props.data.icon} {children}
        </chakraComponents.Option>
    ),
    DropdownIndicator: (props) => (
        <chakraComponents.DropdownIndicator {...props}>
            <GetIcon name="dropdown-arrow-open" size="sm" />
        </chakraComponents.DropdownIndicator>
    ),
};

export interface SelectProps {
    id: string;
    name: string;
    options: SelectOptions[];
    onChange: (selectedOption: SelectOptions) => void;
    value: SelectOptions | undefined;
    closeMenuOnSelect?: boolean;
    isDisabled?: boolean;
}

export const TurnToSelect: FC<SelectProps> = ({
    id,
    name,
    options,
    onChange,
    value,
    closeMenuOnSelect = true,
    isDisabled = false,
}) => (
    <Select
        id={id}
        name={name}
        options={options}
        components={customComponents}
        chakraStyles={chakraStyles}
        onChange={(selectedOption) => onChange(selectedOption as unknown as SelectOptions)}
        value={value}
        closeMenuOnSelect={closeMenuOnSelect}
        isDisabled={isDisabled}
        isSearchable={false}
    />
);
